import { Intent, OverlayToaster, Position } from "@blueprintjs/core";
import LoadingFullScreen from "components/common/LoadingFullScreen";
import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loading } from "types/loadingType";
import {
    segmentTrackCompareScenariosSwap,
    segmentTrackExportCompareResults
} from "./analytics";
import css from "./CompareScenariosPage.module.scss";
import {
    buildCompareCsvString,
    buildFileName,
    downloadCompareCsvZip
} from "./components/compare/compareCsvExport";
import { CompareDetails } from "./components/CompareScenarios/CompareDetails";
import { CompareHeader } from "./components/CompareScenarios/CompareHeader";
import { constraintsCompareColumns } from "./components/CompareScenarios/constraintsCompareColumns";
import { makeProjectsCompareColumns } from "./components/CompareScenarios/projectsCompareColumns";
import { upgradesCompareColumns } from "./components/CompareScenarios/upgradesCompareColumns";
import { useCluster } from "./contexts/ClusterContext";
import {
    CompareScenarioResultsProvider,
    useCompareScenarioResults
} from "./contexts/CompareScenarioResultsContext";
import {
    useProjectFuelTypes,
    useScenarioMetadata,
    useTrackedProject
} from "./contexts/ProjectDataContext";
import { useScenarioMetricsMapping } from "./hooks/useScenarioMetricsMapping";
import { TrackedProject } from "./types/projectType";
import { ScenarioMetricsMapping } from "./types/scenarioMetricsType";
import { ScenarioProject, ScenarioResults } from "./types/scenarioResultType";
import { ScenarioMetadata } from "./types/scenarioType";

const isScenarioProjectExcluded = (p: ScenarioProject): boolean =>
    p.erisSizeMw === 0 && p.nrisSizeMw === 0;

const collectExcludedIds = (
    a: ScenarioResults,
    b: ScenarioResults
): ReadonlySet<string> => {
    const excluded = new Set<string>();
    const projectsA = new Set<string>(a.projects.map((p) => p.projectId));
    const projectsB = new Set<string>(b.projects.map((p) => p.projectId));

    a.projects.forEach((p) => {
        if (isScenarioProjectExcluded(p)) excluded.add(p.projectId);
        if (!projectsB.has(p.projectId)) excluded.add(p.projectId);
    });
    b.projects.forEach((p) => {
        if (isScenarioProjectExcluded(p)) excluded.add(p.projectId);
        if (!projectsA.has(p.projectId)) excluded.add(p.projectId);
    });
    return excluded;
};

const excludeProjectIds = (
    results: ScenarioResults,
    excludedIds: ReadonlySet<string>
): ScenarioResults => ({
    ...results,
    projects: results.projects.filter((p) => !excludedIds.has(p.projectId)),
    allocatedUpgrades: results.allocatedUpgrades.filter(
        (u) => !excludedIds.has(u.projectId)
    ),
    constraints: results.constraints.filter(
        (c) => !excludedIds.has(c.projectId)
    )
});

const ExportCompareResultsToaster = OverlayToaster.create({
    position: Position.TOP
});

export const CompareScenariosPage: React.FC = () => {
    const { projectId } = useParams();
    const cluster = useCluster();
    const [searchParams] = useSearchParams();
    const firstScenarioId = searchParams.get("firstScenarioId") ?? undefined;
    const secondScenarioId = searchParams.get("secondScenarioId") ?? undefined;

    const project = useTrackedProject({
        projectId,
        phase: cluster.studyPhase
    });
    const firstScenario = useScenarioMetadata(projectId, firstScenarioId);
    const secondScenario = useScenarioMetadata(projectId, secondScenarioId);
    const fuelTypeByProjectId = useProjectFuelTypes();
    const scenariosForMetrics = useMemo<Loading<ScenarioMetadata[]>>(() => {
        if (firstScenario === "loading" || secondScenario === "loading") {
            return "loading";
        }
        if (firstScenario === undefined || secondScenario === undefined) {
            return [];
        }
        return [firstScenario, secondScenario];
    }, [firstScenario, secondScenario]);
    const metricsMapping = useScenarioMetricsMapping(
        project,
        scenariosForMetrics
    );

    if (
        project === "loading" ||
        project === undefined ||
        firstScenario === "loading" ||
        firstScenario === undefined ||
        secondScenario === "loading" ||
        secondScenario === undefined ||
        fuelTypeByProjectId === "loading" ||
        metricsMapping === "loading"
    ) {
        return <LoadingFullScreen />;
    }

    return (
        <CompareScenarioResultsProvider
            project={project}
            scenarioIdA={firstScenario.scenarioId}
            scenarioIdB={secondScenario.scenarioId}
        >
            <CompareScenariosBody
                project={project}
                scenarioA={firstScenario}
                scenarioB={secondScenario}
                fuelTypeByProjectId={fuelTypeByProjectId}
                metricsMapping={metricsMapping}
            />
        </CompareScenarioResultsProvider>
    );
};

const CompareScenariosBody: React.FC<{
    project: TrackedProject;
    scenarioA: ScenarioMetadata;
    scenarioB: ScenarioMetadata;
    fuelTypeByProjectId: Readonly<Record<string, string>>;
    metricsMapping: ScenarioMetricsMapping;
}> = ({
    project,
    scenarioA: rawA,
    scenarioB: rawB,
    fuelTypeByProjectId,
    metricsMapping
}) => {
    const {
        resultsA: rawResultsA,
        resultsB: rawResultsB,
        isLoadingRemaining
    } = useCompareScenarioResults();
    const [swapped, setSwapped] = useState(false);

    const [scenarioA, scenarioB] = swapped ? [rawB, rawA] : [rawA, rawB];

    const [allResultsA, allResultsB] = swapped
        ? [rawResultsB, rawResultsA]
        : [rawResultsA, rawResultsB];

    const onSwap = () => {
        segmentTrackCompareScenariosSwap({
            projectId: project.projectId,
            firstScenarioId: scenarioA.scenarioId,
            secondScenarioId: scenarioB.scenarioId
        });
        setSwapped((s) => !s);
    };

    const filtered = useMemo(() => {
        if (allResultsA === "loading" || allResultsB === "loading") {
            return null;
        }
        const excludedIds = collectExcludedIds(allResultsA, allResultsB);
        return {
            a: excludeProjectIds(allResultsA, excludedIds),
            b: excludeProjectIds(allResultsB, excludedIds)
        };
    }, [allResultsA, allResultsB]);

    if (allResultsA === "loading" || allResultsB === "loading") {
        return <LoadingFullScreen />;
    }

    if (filtered === null) {
        return <LoadingFullScreen />;
    }

    const filteredResults = filtered;

    const exportAllResults = async () => {
        const labels = { aLabel: scenarioA.title, bLabel: scenarioB.title };
        const [projects, assignedUpgrades, constraints] = await Promise.all([
            buildCompareCsvString(
                filteredResults.a.projects,
                filteredResults.b.projects,
                makeProjectsCompareColumns(fuelTypeByProjectId),
                labels
            ),
            buildCompareCsvString(
                filteredResults.a.allocatedUpgrades,
                filteredResults.b.allocatedUpgrades,
                upgradesCompareColumns,
                labels
            ),
            buildCompareCsvString(
                filteredResults.a.constraints,
                filteredResults.b.constraints,
                constraintsCompareColumns,
                labels
            )
        ]);
        await downloadCompareCsvZip(
            [
                { name: "projects", csv: projects },
                { name: "assigned-upgrades", csv: assignedUpgrades },
                { name: "constraints", csv: constraints }
            ],
            buildFileName(
                { aLabel: scenarioA.title, bLabel: scenarioB.title },
                "zip",
                project.projectId
            )
        );
        segmentTrackExportCompareResults({
            projectId: project.projectId,
            firstScenarioId: scenarioA.scenarioId,
            secondScenarioId: scenarioB.scenarioId,
            exportName: "all-results"
        });
    };

    const onExport = () => {
        exportAllResults().catch(() =>
            ExportCompareResultsToaster.show({
                message: "Failed to export comparison results",
                intent: Intent.DANGER
            })
        );
    };

    return (
        <div className={css["page"]}>
            <CompareHeader
                project={project}
                scenarioA={scenarioA}
                scenarioB={scenarioB}
                resultsA={allResultsA}
                resultsB={allResultsB}
                metricsMapping={metricsMapping}
                isLoadingRemaining={isLoadingRemaining}
                onSwap={onSwap}
                onExport={onExport}
            />
            <CompareDetails
                project={project}
                resultsA={filtered.a}
                resultsB={filtered.b}
                fuelTypeByProjectId={fuelTypeByProjectId}
                scenarioA={scenarioA}
                scenarioB={scenarioB}
                isLoadingRemaining={isLoadingRemaining}
            />
        </div>
    );
};
